import EqualizerIcon from "@mui/icons-material/Equalizer";
import { Box, Skeleton } from "@mui/material";
import React, { useMemo } from "react";

import { PageContext } from "components/detector/ImageQueryPreview";
import Histogram, { HistogramEntry } from "components/Histogram";
import { Metrics } from "hooks/DetectorDetail/useTopLevelStats";
import { Typography } from "new-components/typography/Typography";
import { borderText, isExplorePage } from "utils/utilityFunctions";

interface BinaryMetricsSectionProps {
  metrics: Metrics | undefined;
  confidence: number;
  pageContext?: PageContext;
}

export function BinaryMetricsSection(props: BinaryMetricsSectionProps) {
  const { metrics, confidence, pageContext = PageContext.DetectorDetails } = props;

  const histogramData: HistogramEntry[] = useMemo(() => {
    if (metrics && metrics?.summary?.confidence_histogram) {
      return metrics.summary.confidence_histogram.map((entry, index) => ({ ...entry, val: index }));
    }
    return [];
  }, [metrics]);

  return (
    <Box className="flex justify-between h-[3.125rem] md:h-[4.7rem] mt-2">
      {histogramData.length > 0 ? (
        <Box className="flex w-full">
          <Box
            className="flex flex-col justify-between items-center p-2 md:p-0 w-[12.5%]"
            sx={{ backgroundColor: ["#6950E680", "inherit"], minWidth: ["2.313rem", "unset"] }}
          >
            <Typography
              fontWeight="semibold"
              className="tracking-widest mt-1 order-2 md:order-1"
              sx={{
                color: ["white", "#ADA2EE"],
                textShadow: borderText("#4830BF"),
              }}
            >
              {metrics?.summary?.class_counts?.total?.YES || 0}
            </Typography>

            <Typography
              fontWeight="semibold"
              className="order-1 md:order-2"
              sx={{ color: ["white", "#6950E6"] }}
            >
              YES
            </Typography>
          </Box>
          <Box className="w-3/4">
            <Histogram histogramData={histogramData} confidenceThreshold={confidence} isPreview />
          </Box>
          <Box
            className="flex flex-col justify-between items-center p-2 md:p-0 w-[12.5%]"
            sx={{ backgroundColor: ["#DE7AF0", "inherit"], minWidth: ["2.313rem", "unset"] }}
          >
            <Typography
              fontWeight="semibold"
              className="tracking-widest mt-1 order-2 md:order-1"
              sx={{
                color: ["white", "#DE7AF0"],
                textShadow: borderText("#BA00E6"),
              }}
            >
              {metrics?.summary?.class_counts?.total?.NO || 0}
            </Typography>

            <Typography
              fontWeight="semibold"
              className="order-1 md:order-2"
              sx={{ color: ["white", "#CA00EB"] }}
            >
              NO
            </Typography>
          </Box>
          {/* )} */}
        </Box>
      ) : (
        <div
          style={{ position: "relative", height: `${isExplorePage(pageContext) ? "100px" : "75px"}` }}
          className="w-full"
        >
          <Skeleton
            animation={false}
            variant="rectangular"
            sx={{ background: "#E5E9ED", borderRadius: "15px" }}
            height={isExplorePage(pageContext) ? "100px" : "75px"}
          />
          <EqualizerIcon
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              background: "#C8CDD3",
              fontSize: "4rem",
              borderRadius: "50%",
              padding: "0.5rem",
              color: "#E5E9ED",
            }}
          />
        </div>
      )}
    </Box>
  );
}
